/**
 * Leviathan - Lifting Form Page
 * Quasar Prime LLC
 *
 * Create and edit liftings with temperature correction calculator
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { parseApiError } from '@/utils/errorHandler';
import {
  ArrowLeft,
  Save,
  Loader2,
  Plus,
  X,
  Thermometer,
  Calculator,
} from 'lucide-react';
import { API_URL } from '@/config/api';

interface LiftingItem {
  productId: string;
  grossGallons: string;
  temperature: string;
  gravity: string;
  netGallons: string;
  billedGallons: string;
  rackPrice: string;
  freightCost: string;
  otherCosts: string;
  federalExciseTax: string;
  stateFuelTax: string;
}

interface Terminal {
  id: string;
  name: string;
  tcn: string | null;
  city: string | null;
  state: string | null;
}

interface Supplier {
  id: string;
  name: string;
}

interface Truck {
  id: string;
  unitNumber: string;
}

interface Driver {
  id: string;
  firstName: string;
  lastName: string;
}

interface Product {
  id: string;
  name: string;
  sku: string;
}

const emptyItem: LiftingItem = {
  productId: '',
  grossGallons: '',
  temperature: '',
  gravity: '35',
  netGallons: '',
  billedGallons: '',
  rackPrice: '',
  freightCost: '',
  otherCosts: '',
  federalExciseTax: '',
  stateFuelTax: '',
};

export default function LiftingForm() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    terminalId: searchParams.get('terminalId') || '',
    bolNumber: '',
    liftDate: new Date().toISOString().split('T')[0],
    supplierId: '',
    supplierInvoiceNum: '',
    supplierInvoiceDate: '',
    truckId: '',
    driverId: '',
    carrierName: '',
    carrierFein: '',
    notes: '',
  });

  const [items, setItems] = useState<LiftingItem[]>([{ ...emptyItem }]);
  const [error, setError] = useState<string | null>(null);

  // Fetch terminals
  const { data: terminalsData } = useQuery({
    queryKey: ['terminals-list'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/terminals?isActive=true&limit=100`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch terminals');
      return res.json();
    },
  });
  const terminals: Terminal[] = terminalsData?.items || [];

  // Fetch suppliers
  const { data: suppliersData } = useQuery({
    queryKey: ['suppliers-list'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/suppliers?limit=100`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch suppliers');
      return res.json();
    },
  });
  const suppliers: Supplier[] = suppliersData?.items || [];

  // Fetch trucks
  const { data: trucksData } = useQuery({
    queryKey: ['trucks-list'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/trucks?limit=100`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch trucks');
      return res.json();
    },
  });
  const trucks: Truck[] = trucksData?.items || [];

  // Fetch drivers
  const { data: driversData } = useQuery({
    queryKey: ['drivers-list'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/drivers?limit=100`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch drivers');
      return res.json();
    },
  });
  const drivers: Driver[] = driversData?.items || [];

  // Fetch products
  const { data: productsData } = useQuery({
    queryKey: ['products-list'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/inventory?limit=100`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch products');
      return res.json();
    },
  });
  const products: Product[] = productsData?.items || [];

  // Fetch existing lifting for edit
  const { data: existingLifting, isLoading: isLoadingLifting } = useQuery({
    queryKey: ['lifting', id],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/terminals/liftings/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch lifting');
      return res.json();
    },
    enabled: isEditing,
  });

  // Populate form when editing
  useEffect(() => {
    if (existingLifting?.lifting) {
      const lifting = existingLifting.lifting;
      setFormData({
        terminalId: lifting.terminalId || '',
        bolNumber: lifting.bolNumber || '',
        liftDate: lifting.liftDate?.split('T')[0] || '',
        supplierId: lifting.supplierId || '',
        supplierInvoiceNum: lifting.supplierInvoiceNum || '',
        supplierInvoiceDate: lifting.supplierInvoiceDate?.split('T')[0] || '',
        truckId: lifting.truckId || '',
        driverId: lifting.driverId || '',
        carrierName: lifting.carrierName || '',
        carrierFein: lifting.carrierFein || '',
        notes: lifting.notes || '',
      });

      if (lifting.items?.length > 0) {
        setItems(lifting.items.map((item: any) => ({
          productId: item.productId,
          grossGallons: item.grossGallons?.toString() || '',
          temperature: item.temperature?.toString() || '',
          gravity: item.gravity?.toString() || '35',
          netGallons: item.netGallons?.toString() || '',
          billedGallons: item.billedGallons?.toString() || '',
          rackPrice: item.rackPrice?.toString() || '',
          freightCost: item.freightCost?.toString() || '',
          otherCosts: item.otherCosts?.toString() || '',
          federalExciseTax: item.federalExciseTax?.toString() || '',
          stateFuelTax: item.stateFuelTax?.toString() || '',
        })));
      }
    }
  }, [existingLifting]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (payload: any) => {
      const url = isEditing
        ? `${API_URL}/api/terminals/liftings/${id}`
        : `${API_URL}/api/terminals/liftings`;

      const res = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorMessage = await parseApiError(res);
        throw new Error(errorMessage);
      }
      return res.json();
    },
    onSuccess: (data) => {
      navigate(`/liftings/${data.lifting?.id || id}`);
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  /**
   * Temperature correction calculation (ASTM Table 6B simplified)
   * Uses coefficient of thermal expansion based on API gravity
   */
  const calculateNetGallons = (grossGallons: number, temperature: number, gravity: number): number => {
    // Default to 60°F if no temperature (no correction needed)
    if (!temperature || temperature === 60) return grossGallons;

    // Coefficient of thermal expansion based on API gravity
    let alpha: number;
    if (gravity >= 55) {
      alpha = 0.00066; // Gasoline (lighter products)
    } else if (gravity >= 35) {
      alpha = 0.00044; // Diesel/kerosene
    } else {
      alpha = 0.00040; // Heavy fuel oils
    }

    // Temperature correction factor
    const correctionFactor = 1 - ((temperature - 60) * alpha);
    return Math.round(grossGallons * correctionFactor * 10) / 10;
  };

  const handleItemChange = (index: number, field: keyof LiftingItem, value: string) => {
    const newItems = [...items];
    const currentItem = newItems[index];
    if (!currentItem) return;

    newItems[index] = { ...currentItem, [field]: value };

    // Auto-calculate net gallons when gross, temperature, or gravity change
    if (field === 'grossGallons' || field === 'temperature' || field === 'gravity') {
      const updatedItem = newItems[index];
      if (!updatedItem) return;

      const gross = parseFloat(updatedItem.grossGallons) || 0;
      const temp = parseFloat(updatedItem.temperature) || 60;
      const gravity = parseFloat(updatedItem.gravity) || 35;

      if (gross > 0) {
        const net = calculateNetGallons(gross, temp, gravity);
        updatedItem.netGallons = net.toFixed(1);
        // Default billed gallons to net gallons
        if (!updatedItem.billedGallons) {
          updatedItem.billedGallons = net.toFixed(1);
        }
      }
    }

    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, { ...emptyItem }]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const calculateItemTotal = (item: LiftingItem) => {
    const billed = parseFloat(item.billedGallons) || 0;
    const rack = parseFloat(item.rackPrice) || 0;
    const freight = parseFloat(item.freightCost) || 0;
    const other = parseFloat(item.otherCosts) || 0;
    return billed * (rack + freight + other);
  };

  const calculateTotals = () => {
    return items.reduce((acc, item) => ({
      gross: acc.gross + (parseFloat(item.grossGallons) || 0),
      net: acc.net + (parseFloat(item.netGallons) || 0),
      billed: acc.billed + (parseFloat(item.billedGallons) || 0),
      total: acc.total + calculateItemTotal(item),
    }), { gross: 0, net: 0, billed: 0, total: 0 });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const validItems = items.filter(item => item.productId && item.grossGallons);
    if (validItems.length === 0) {
      setError('At least one line item is required');
      return;
    }

    if (!formData.terminalId) {
      setError('Terminal is required');
      return;
    }

    if (!formData.bolNumber) {
      setError('Terminal BOL number is required');
      return;
    }

    const payload = {
      terminalId: formData.terminalId,
      bolNumber: formData.bolNumber,
      liftDate: formData.liftDate,
      supplierId: formData.supplierId || null,
      supplierInvoiceNum: formData.supplierInvoiceNum || null,
      supplierInvoiceDate: formData.supplierInvoiceDate || null,
      truckId: formData.truckId || null,
      driverId: formData.driverId || null,
      carrierName: formData.carrierName || null,
      carrierFein: formData.carrierFein || null,
      notes: formData.notes || null,
      items: validItems.map(item => ({
        productId: item.productId,
        grossGallons: parseFloat(item.grossGallons) || 0,
        temperature: item.temperature ? parseFloat(item.temperature) : null,
        gravity: item.gravity ? parseFloat(item.gravity) : null,
        netGallons: parseFloat(item.netGallons) || 0,
        billedGallons: parseFloat(item.billedGallons) || 0,
        rackPrice: parseFloat(item.rackPrice) || 0,
        freightCost: parseFloat(item.freightCost) || 0,
        otherCosts: parseFloat(item.otherCosts) || 0,
        federalExciseTax: parseFloat(item.federalExciseTax) || 0,
        stateFuelTax: parseFloat(item.stateFuelTax) || 0,
      })),
    };

    saveMutation.mutate(payload);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const totals = calculateTotals();
  const selectedTerminal = terminals.find(t => t.id === formData.terminalId);

  if (isEditing && isLoadingLifting) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-4 mb-2">
            <Link
              to={isEditing ? `/liftings/${id}` : '/liftings'}
              className="text-slate-400 hover:text-amber-400 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-2xl font-bold text-slate-100">
              {isEditing ? 'Edit Lifting' : 'New Lifting'}
            </h1>
          </div>
          <p className="text-slate-400 text-sm">
            {isEditing ? 'Update terminal BOL information' : 'Record a fuel pickup from terminal'}
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/50 text-red-400 p-4 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Terminal & BOL */}
        <Panel title="Terminal & BOL Information">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-slate-400 text-xs uppercase tracking-wider mb-2">
                Terminal *
              </label>
              <select
                value={formData.terminalId}
                onChange={(e) => setFormData(prev => ({ ...prev, terminalId: e.target.value }))}
                className="w-full bg-slate-900/50 border border-slate-700 focus:border-amber-500 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                required
              >
                <option value="">Select terminal...</option>
                {terminals.map(terminal => (
                  <option key={terminal.id} value={terminal.id}>
                    {terminal.name} ({terminal.city}, {terminal.state})
                  </option>
                ))}
              </select>
              {selectedTerminal?.tcn && (
                <p className="text-amber-400 text-xs mt-1 font-mono">{selectedTerminal.tcn}</p>
              )}
            </div>

            <div>
              <label className="block text-slate-400 text-xs uppercase tracking-wider mb-2">
                Terminal BOL # *
              </label>
              <input
                type="text"
                value={formData.bolNumber}
                onChange={(e) => setFormData(prev => ({ ...prev, bolNumber: e.target.value }))}
                className="w-full bg-slate-900/50 border border-slate-700 focus:border-amber-500 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                placeholder="Terminal's BOL number"
                required
              />
            </div>

            <div>
              <label className="block text-slate-400 text-xs uppercase tracking-wider mb-2">
                Lift Date *
              </label>
              <input
                type="date"
                value={formData.liftDate}
                onChange={(e) => setFormData(prev => ({ ...prev, liftDate: e.target.value }))}
                className="w-full bg-slate-900/50 border border-slate-700 focus:border-amber-500 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                required
              />
            </div>
          </div>
        </Panel>

        {/* Supplier */}
        <Panel title="Supplier Information">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-slate-400 text-xs uppercase tracking-wider mb-2">
                Supplier
              </label>
              <select
                value={formData.supplierId}
                onChange={(e) => setFormData(prev => ({ ...prev, supplierId: e.target.value }))}
                className="w-full bg-slate-900/50 border border-slate-700 focus:border-amber-500 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              >
                <option value="">Select supplier...</option>
                {suppliers.map(supplier => (
                  <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-slate-400 text-xs uppercase tracking-wider mb-2">
                Supplier Invoice #
              </label>
              <input
                type="text"
                value={formData.supplierInvoiceNum}
                onChange={(e) => setFormData(prev => ({ ...prev, supplierInvoiceNum: e.target.value }))}
                className="w-full bg-slate-900/50 border border-slate-700 focus:border-amber-500 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>

            <div>
              <label className="block text-slate-400 text-xs uppercase tracking-wider mb-2">
                Supplier Invoice Date
              </label>
              <input
                type="date"
                value={formData.supplierInvoiceDate}
                onChange={(e) => setFormData(prev => ({ ...prev, supplierInvoiceDate: e.target.value }))}
                className="w-full bg-slate-900/50 border border-slate-700 focus:border-amber-500 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
          </div>
        </Panel>

        {/* Carrier */}
        <Panel title="Carrier Information">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <label className="block text-slate-400 text-xs uppercase tracking-wider mb-2">
                Truck
              </label>
              <select
                value={formData.truckId}
                onChange={(e) => setFormData(prev => ({ ...prev, truckId: e.target.value }))}
                className="w-full bg-slate-900/50 border border-slate-700 focus:border-amber-500 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              >
                <option value="">Select truck...</option>
                {trucks.map(truck => (
                  <option key={truck.id} value={truck.id}>{truck.unitNumber}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-slate-400 text-xs uppercase tracking-wider mb-2">
                Driver
              </label>
              <select
                value={formData.driverId}
                onChange={(e) => setFormData(prev => ({ ...prev, driverId: e.target.value }))}
                className="w-full bg-slate-900/50 border border-slate-700 focus:border-amber-500 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              >
                <option value="">Select driver...</option>
                {drivers.map(driver => (
                  <option key={driver.id} value={driver.id}>
                    {driver.firstName} {driver.lastName}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-slate-400 text-xs uppercase tracking-wider mb-2">
                3rd Party Carrier
              </label>
              <input
                type="text"
                value={formData.carrierName}
                onChange={(e) => setFormData(prev => ({ ...prev, carrierName: e.target.value }))}
                className="w-full bg-slate-900/50 border border-slate-700 focus:border-amber-500 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                placeholder="If not own truck"
              />
            </div>

            <div>
              <label className="block text-slate-400 text-xs uppercase tracking-wider mb-2">
                Carrier FEIN
              </label>
              <input
                type="text"
                value={formData.carrierFein}
                onChange={(e) => setFormData(prev => ({ ...prev, carrierFein: e.target.value }))}
                className="w-full bg-slate-900/50 border border-slate-700 focus:border-amber-500 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                placeholder="XX-XXXXXXX"
              />
            </div>
          </div>
        </Panel>

        {/* Line Items */}
        <Panel title="Line Items - Temperature Correction">
          <div className="flex items-center gap-2 mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <Calculator className="w-5 h-5 text-blue-400" />
            <span className="text-blue-300 text-sm">
              Net gallons are auto-calculated using ASTM Table 6B temperature correction (60°F standard)
            </span>
          </div>

          <div className="space-y-4 overflow-x-auto">
            {/* Header */}
            <div className="grid grid-cols-12 gap-2 text-slate-500 text-xs uppercase tracking-wider pb-2 border-b border-slate-700 min-w-[900px]">
              <div className="col-span-2">Product</div>
              <div className="col-span-1">Gross Gal</div>
              <div className="col-span-1">Temp °F</div>
              <div className="col-span-1">API Gravity</div>
              <div className="col-span-1">Net Gal</div>
              <div className="col-span-1">Rack $/gal</div>
              <div className="col-span-1">Freight</div>
              <div className="col-span-1">Fed Tax</div>
              <div className="col-span-1">State Tax</div>
              <div className="col-span-1 text-right">Total</div>
              <div className="col-span-1"></div>
            </div>

            {items.map((item, index) => (
              <div key={index} className="grid grid-cols-12 gap-2 items-center min-w-[900px]">
                <select
                  value={item.productId}
                  onChange={(e) => handleItemChange(index, 'productId', e.target.value)}
                  className="col-span-2 bg-slate-900/50 border border-slate-700 focus:border-amber-500 rounded px-2 py-2 text-slate-100 text-sm focus:outline-none"
                >
                  <option value="">Select...</option>
                  {products.map(product => (
                    <option key={product.id} value={product.id}>{product.name}</option>
                  ))}
                </select>
                <input
                  type="number"
                  value={item.grossGallons}
                  onChange={(e) => handleItemChange(index, 'grossGallons', e.target.value)}
                  placeholder="0"
                  className="col-span-1 bg-slate-900/50 border border-slate-700 focus:border-amber-500 rounded px-2 py-2 text-slate-100 text-sm focus:outline-none"
                />
                <div className="col-span-1 relative">
                  <input
                    type="number"
                    value={item.temperature}
                    onChange={(e) => handleItemChange(index, 'temperature', e.target.value)}
                    placeholder="60"
                    className="w-full bg-slate-900/50 border border-slate-700 focus:border-amber-500 rounded px-2 py-2 text-slate-100 text-sm focus:outline-none"
                  />
                  <Thermometer className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-500" />
                </div>
                <input
                  type="number"
                  value={item.gravity}
                  onChange={(e) => handleItemChange(index, 'gravity', e.target.value)}
                  placeholder="35"
                  className="col-span-1 bg-slate-900/50 border border-slate-700 focus:border-amber-500 rounded px-2 py-2 text-slate-100 text-sm focus:outline-none"
                />
                <input
                  type="number"
                  value={item.netGallons}
                  onChange={(e) => handleItemChange(index, 'netGallons', e.target.value)}
                  placeholder="0"
                  className="col-span-1 bg-amber-500/10 border border-amber-500/50 rounded px-2 py-2 text-amber-400 font-semibold text-sm focus:outline-none"
                  readOnly
                />
                <input
                  type="number"
                  value={item.rackPrice}
                  onChange={(e) => handleItemChange(index, 'rackPrice', e.target.value)}
                  placeholder="0.00"
                  step="0.0001"
                  className="col-span-1 bg-slate-900/50 border border-slate-700 focus:border-amber-500 rounded px-2 py-2 text-slate-100 text-sm focus:outline-none"
                />
                <input
                  type="number"
                  value={item.freightCost}
                  onChange={(e) => handleItemChange(index, 'freightCost', e.target.value)}
                  placeholder="0.00"
                  step="0.0001"
                  className="col-span-1 bg-slate-900/50 border border-slate-700 focus:border-amber-500 rounded px-2 py-2 text-slate-100 text-sm focus:outline-none"
                />
                <input
                  type="number"
                  value={item.federalExciseTax}
                  onChange={(e) => handleItemChange(index, 'federalExciseTax', e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                  className="col-span-1 bg-slate-900/50 border border-slate-700 focus:border-amber-500 rounded px-2 py-2 text-slate-100 text-sm focus:outline-none"
                />
                <input
                  type="number"
                  value={item.stateFuelTax}
                  onChange={(e) => handleItemChange(index, 'stateFuelTax', e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                  className="col-span-1 bg-slate-900/50 border border-slate-700 focus:border-amber-500 rounded px-2 py-2 text-slate-100 text-sm focus:outline-none"
                />
                <div className="col-span-1 text-right text-amber-400 font-semibold text-sm">
                  {formatCurrency(calculateItemTotal(item))}
                </div>
                <button
                  type="button"
                  onClick={() => removeItem(index)}
                  className="col-span-1 flex justify-center text-slate-500 hover:text-red-400 transition-colors"
                  disabled={items.length === 1}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}

            <button
              type="button"
              onClick={addItem}
              className="flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm"
            >
              <Plus className="w-4 h-4" />
              Add Line Item
            </button>

            {/* Totals */}
            <div className="mt-6 pt-6 border-t-2 border-amber-500/50">
              <div className="grid grid-cols-4 gap-4 text-center">
                <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-4">
                  <div className="text-slate-500 text-xs uppercase mb-1">Gross Gallons</div>
                  <div className="text-slate-100 text-lg">{totals.gross.toLocaleString()}</div>
                </div>
                <div className="bg-amber-500/10 border border-amber-500/50 rounded-lg p-4">
                  <div className="text-amber-400 text-xs uppercase mb-1">Net Gallons (60°F)</div>
                  <div className="text-amber-400 text-lg font-semibold">{totals.net.toLocaleString()}</div>
                </div>
                <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-4">
                  <div className="text-slate-500 text-xs uppercase mb-1">Temp Correction</div>
                  <div className="text-slate-300 text-lg">
                    {totals.gross > totals.net ? '-' : '+'}
                    {Math.abs(totals.gross - totals.net).toFixed(1)}
                  </div>
                </div>
                <div className="bg-amber-500/10 border border-amber-500/50 rounded-lg p-4">
                  <div className="text-amber-400 text-xs uppercase mb-1">Total Cost</div>
                  <div className="text-amber-400 text-xl font-bold">{formatCurrency(totals.total)}</div>
                </div>
              </div>
            </div>
          </div>
        </Panel>

        {/* Notes */}
        <Panel title="Notes">
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            rows={3}
            className="w-full bg-slate-900/50 border border-slate-700 focus:border-amber-500 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50 resize-none"
            placeholder="Additional notes..."
          />
        </Panel>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <Link
            to={isEditing ? `/liftings/${id}` : '/liftings'}
            className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 rounded-lg transition-colors"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={saveMutation.isPending}
            className="flex items-center gap-2 px-6 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {saveMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {isEditing ? 'Update Lifting' : 'Create Lifting'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="relative bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber-500/50 rounded-tl-xl" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber-500/50 rounded-tr-xl" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber-500/50 rounded-bl-xl" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber-500/50 rounded-br-xl" />

      <div className="p-6">
        <h3 className="text-lg font-semibold text-slate-100 uppercase tracking-wide mb-6 pb-3 border-b border-amber-500/30">
          {title}
        </h3>
        {children}
      </div>
    </div>
  );
}
